import _ from 'lodash';
import monotonicTimestamp from 'monotonic-timestamp';
import { $each, $merge, $nav, $set, update } from 'qim';

import ActionTypes from 'app/account/ActionTypes';
import { createReducer } from 'app/common/utils/ReduxUtils';

const initialState = {
  subscriptions: undefined,
  notifications: {},
  actionBanners: {},
};

const $eachSubscription = channelKeys =>
  $nav(['subscriptions', $each, sub => _.includes(channelKeys, sub.key)]);

const handlers = {
  [ActionTypes.ADD_ACCOUNT_BANNER](state, { status, message }) {
    return update(
      [
        'actionBanners',
        $merge({
          [monotonicTimestamp()]: {
            status,
            message,
          },
        }),
      ],
      state
    );
  },

  [ActionTypes.ACKNOWLEDGE_ACCOUNT_BANNER](state, { key }) {
    return update(['actionBanners', key, 'acknowledged', $set(true)], state);
  },

  [ActionTypes.LOAD_ACCOUNT_NOTIFICATIONS_DONE](state, { notifications }) {
    notifications = JSON.parse(JSON.stringify(notifications));
    notifications = _.fromPairs(
      _.map(notifications, (value, key) => {
        return [key, { value }];
      })
    );
    return update(['notifications', $set(notifications)], state);
  },

  [ActionTypes.SET_ACCOUNT_SUMMARY_SCHEDULE](state, { value }) {
    return update(
      [
        'notifications',
        'summary_schedule',
        ['value', $set(value)],
        ['loading', $set(true)],
      ],
      state
    );
  },

  [ActionTypes.SET_ACCOUNT_SUMMARY_SCHEDULE_DONE](state) {
    return update(
      ['notifications', 'summary_schedule', 'loading', $set(false)],
      state
    );
  },

  [ActionTypes.SET_ACCOUNT_TRIGGER_ALERTS](state, { value }) {
    return update(
      [
        'notifications',
        'alert_triggers',
        ['value', $set(value)],
        ['loading', $set(true)],
      ],
      state
    );
  },

  [ActionTypes.SET_ACCOUNT_TRIGGER_ALERTS_DONE](state) {
    return update(
      ['notifications', 'alert_triggers', 'loading', $set(false)],
      state
    );
  },

  [ActionTypes.SET_ACCOUNT_ACTION_ALERTS](state, { value }) {
    return update(
      [
        'notifications',
        'alert_actions',
        ['value', $set(value)],
        ['loading', $set(true)],
      ],
      state
    );
  },

  [ActionTypes.SET_ACCOUNT_ACTION_ALERTS_DONE](state) {
    return update(
      ['notifications', 'alert_actions', 'loading', $set(false)],
      state
    );
  },

  [ActionTypes.LOAD_ACCOUNT_SUBSCRIPTIONS_DONE](state, { subscriptions }) {
    return update(['subscriptions', $set(subscriptions)], state);
  },

  [ActionTypes.ADD_ACCOUNT_SUBSCRIPTIONS](state, { channelKeys }) {
    return update(
      [
        $eachSubscription(channelKeys),
        ['subscribed', $set(true)],
        ['loading', $set(true)],
      ],
      state
    );
  },

  [ActionTypes.ADD_ACCOUNT_SUBSCRIPTIONS_DONE](state, { channelKeys }) {
    return update(
      [$eachSubscription(channelKeys), 'loading', $set(false)],
      state
    );
  },

  [ActionTypes.ADD_ACCOUNT_SUBSCRIPTIONS_FAIL](state, { channelKeys }) {
    const channelDesc = channelKeys.join(', ');
    return handlers[ActionTypes.ADD_ACCOUNT_BANNER](state, {
      status: 'error',
      message: `There was an error subscribing you to ${channelDesc} emails.`,
    });
  },

  [ActionTypes.REMOVE_ACCOUNT_SUBSCRIPTIONS](state, { channelKeys }) {
    return update(
      [
        $eachSubscription(channelKeys),
        $merge({ subscribed: false, loading: true }),
      ],
      state
    );
  },

  [ActionTypes.REMOVE_ACCOUNT_SUBSCRIPTIONS_DONE](state, { channelKeys }) {
    return update(
      [$eachSubscription(channelKeys), 'loading', $set(false)],
      state
    );
  },

  [ActionTypes.REMOVE_ACCOUNT_SUBSCRIPTIONS_FAIL](state, { channelKeys }) {
    const channelDesc = channelKeys.join(', ');
    return handlers[ActionTypes.ADD_ACCOUNT_BANNER](state, {
      status: 'error',
      message: `There was an error unsubscribing you from ${channelDesc} emails.`,
    });
  },

  [ActionTypes.ADD_ACCOUNT_BANNER_NOTIFICATIONS_ERROR](state) {
    return handlers[ActionTypes.ADD_ACCOUNT_BANNER](state, {
      status: 'error',
      message: 'There was an error setting your notification preferences.',
    });
  },

  [ActionTypes.DECODE_MESSAGE_DONE](state, { result }) {
    if (result.message) {
      return handlers[ActionTypes.ADD_ACCOUNT_BANNER](state, {
        status: 'success',
        message: result.message,
      });
    }

    return state;
  },
};

export default createReducer(handlers, initialState);
